"use client";

import { useState, useEffect } from "react";
import Image from "next/image";
import { cn } from "@/lib/utils";

interface WebsiteFaviconProps {
  url: string;
  size?: number;
  className?: string;
}

export function WebsiteFavicon({ url, size = 16, className }: WebsiteFaviconProps) {
  const [faviconUrl, setFaviconUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (!url) {
      setIsLoading(false);
      setHasError(true);
      return;
    }

    const fetchFavicon = async () => {
      setIsLoading(true);
      setHasError(false);

      try {
        // Use API to resolve stable same-origin URL for Next/Image optimizer
        const response = await fetch(`/api/favicon?url=${encodeURIComponent(url)}`);

        if (response.ok) {
          const data = await response.json();
          if (data.faviconUrl) {
            setFaviconUrl(data.faviconUrl);
            setIsLoading(false);
            return;
          }
        }

        // If API fails, fallback to error state
        setHasError(true);
        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching favicon:", error);
        setHasError(true);
        setIsLoading(false);
      }
    };

    fetchFavicon();
  }, [url]);

  if (isLoading) {
    return null;
  }

  if (hasError || !faviconUrl) {
    return <div className={cn("h-4 w-4 animate-pulse rounded-full bg-gray-200", className)} />;
  }

  return (
    <Image
      src={faviconUrl}
      alt={`Favicon for ${url}`}
      width={size}
      height={size}
      className={cn("rounded", className)}
      onError={() => {
        setHasError(true);
        setFaviconUrl(null);
      }}
      // Let Next/Image optimizer cache and serve the image
    />
  );
}
